import { format } from "date-fns";
import { MapPin, CalendarClock, Navigation, Package } from "lucide-react";
import { StatusBadge } from "./StatusBadge";
import { StatusTimeline } from "./StatusTimeline";

const fmtDate = (d) => format(new Date(`${d}T12:00:00`), "EEE, MMM d");
const windowText = (w) => {
  if (!w || (!w.start && !w.end)) return null;
  if (w.start && w.end && w.start !== w.end) return `${fmtDate(w.start)} – ${fmtDate(w.end)}`;
  return fmtDate(w.start || w.end);
};

const Info = ({ icon: I, label, value, testid, accent }) => (
  <div className="bg-white p-5">
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500"><I className="w-3.5 h-3.5 text-orange-600" />{label}</div>
    <div className={`mt-2 font-display font-bold text-xl leading-tight ${accent ? "text-brand-600" : "text-slate-900"}`} data-testid={testid}>{value || "—"}</div>
  </div>
);

const History = ({ milestones }) => (
  <ol className="relative border-l border-slate-200 ml-2" data-testid="milestone-history">
    {milestones.map((m, i) => (
      <li key={`${m.timestamp}-${i}`} className="pl-6 pb-7 last:pb-0 relative" data-testid={`milestone-${i}`}>
        <span className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 ${i === 0 ? "bg-amber-500" : "bg-slate-400"}`} />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-display font-bold uppercase text-slate-900 tracking-wide">{m.status}</span>
          <span className="font-mono text-[11px] text-slate-500">{format(new Date(m.timestamp), "MMM d, yyyy · h:mm a")}</span>
        </div>
        {m.note && <p className="mt-1.5 text-sm text-slate-600">{m.note}</p>}
        {m.location && <p className="mt-1 text-xs text-brand-600 flex items-center gap-1"><MapPin className="w-3 h-3" />{m.location}</p>}
      </li>
    ))}
  </ol>
);

export const TrackResult = ({ data }) => {
  const win = windowText(data.delivery_window);
  return (
    <div className="space-y-8 rise" data-testid="track-result">
      <div className="panel p-6 sm:p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Tracking number</div>
            <div className="font-mono font-bold text-3xl sm:text-4xl text-slate-900 tracking-wider mt-1" data-testid="track-result-number">{data.tracking_number}</div>
            <div className="mt-2 flex items-center gap-2 text-slate-700 text-sm" data-testid="track-result-item"><Package className="w-4 h-4 text-orange-600" />{data.item_summary}{data.expedited ? " · Expedited" : ""}</div>
          </div>
          <StatusBadge status={data.status} testid="track-result-status" />
        </div>
        <div className="mt-8 flex items-center gap-4 font-display uppercase font-bold text-xl sm:text-2xl text-slate-900" data-testid="track-result-route">
          <span>{data.origin.city}, {data.origin.state}</span>
          <span className="flex-1 h-px bg-gradient-to-r from-amber-500 via-amber-500/40 to-amber-500 relative"><Navigation className="w-4 h-4 text-orange-600 absolute left-1/2 -top-2 rotate-90" /></span>
          <span>{data.destination.city}, {data.destination.state}</span>
        </div>
      </div>
      <div className="panel p-6 sm:p-8"><StatusTimeline data={data} /></div>
      <div className="grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
        <Info icon={MapPin} label="Latest known location" value={data.current_location} testid="track-result-location" />
        <Info icon={CalendarClock} label={data.status === "Delivered" ? "Delivered window" : "Delivery window / ETA"} value={win || "Confirmed before dispatch"} testid="track-result-window" accent={!!win} />
      </div>
      {data.delivery_window?.note && <p className="text-sm text-slate-600 -mt-4" data-testid="track-result-window-note">{data.delivery_window.note}</p>}
      <div className="panel p-6 sm:p-8">
        <div className="font-display font-bold uppercase text-slate-900 text-2xl mb-6">Milestone history</div>
        <History milestones={data.milestones} />
      </div>
    </div>
  );
};
